// Page-level FAQ sets (3-5 questions each) for landing pages, plus the
// generated FAQs every product page gets. Answers only restate facts already
// published elsewhere on the site (features.js, knowledge.js, the Refund
// Policy, pricing data) - no new claims.
import { COMPANY, PRODUCT_FAMILIES, PRODUCTS_KB, INDUSTRIES } from "./knowledge";
import {
  ADDONS,
  ALL_PLANS,
  CHANNEL_PRICING,
  channelPrice,
  formatINR,
  PLANS,
  planDisplayName,
  planHighlights,
  planSections,
  PRICING_PROMOTION,
} from "./content";
import { CURRENCIES } from "./currencies";

const OTHER_CURRENCIES = CURRENCIES.filter((c) => c.code !== "INR").map((c) => c.code);

// --- Company facts shared by several FAQ sets ------------------------------
export const SERVICE_AREA =
  `MagicBolt AI is headquartered in Bhopal, India, and serves agencies and businesses worldwide. ` +
  `The platform is web-based and fully cloud-hosted, so it can be used from any country. ` +
  `Prices are set in Indian Rupees (INR) and shown converted into ${OTHER_CURRENCIES.length} other currencies (${OTHER_CURRENCIES.join(", ")}) on the pricing page.`;

// Summary of src/components/sections/RefundPolicyPage.jsx - keep in sync.
export const REFUND_SUMMARY =
  `Refunds follow the MagicBolt AI Refund Policy. Yearly plans can be cancelled within 7 days of the first purchase for a full refund, provided the platform has seen minimal use; after 7 days a yearly plan is non-refundable but stays active until the end of the paid term. ` +
  `Monthly plans can be cancelled at any time to stop the next charge, but the month already billed is not refunded. ` +
  `Approved refunds are returned to the original payment method within 7-10 business days. ` +
  `One-time (lifetime) purchases can be refunded in full within 30 days of purchase; after 30 days they are non-refundable.`;

const PRICE_FROM = formatINR(channelPrice(CHANNEL_PRICING.stops[0].paid, "yearly"));
const UNLIMITED_NAMES = PLANS.map((p) => planDisplayName(p.name)).join(", ");

// --- Landing pages ----------------------------------------------------------
export const FEATURES_PAGE_FAQS = [
  {
    q: "What products does MagicBolt AI include?",
    a: PRODUCT_FAMILIES.map(
      (f) => `${f.name}: ${PRODUCTS_KB.filter((p) => p.family === f.id).map((p) => p.name).join(", ")}.`
    ).join(" "),
  },
  {
    q: "Which channels can I connect?",
    a: "Google Business Profile, Facebook, Instagram, WhatsApp (through WhatsApp API CRM) and WordPress. AEO + GEO checks then show whether AI assistants such as ChatGPT, Gemini and Perplexity can find and recommend the business.",
  },
  {
    q: "Do I need technical skills to use these features?",
    a: "No. Channels connect in a few clicks with secure sign-in, the automations come pre-built, and everything is managed from a visual dashboard.",
  },
  {
    q: "Can I resell these features under my own brand?",
    a: "Yes. Every feature is part of Agency OS, the white-label platform: your logo, colours, login page and domain, at prices you set. Clients never see the MagicBolt AI name.",
  },
  {
    q: "How do I know which feature I need?",
    a: "Describe the problem you want to solve in the Product Finder and it will show the product that handles it, why, and the features involved.",
  },
];

export const AGENCY_OS_FAQS = [
  {
    q: "What is Agency OS?",
    a: `Agency OS is the complete MagicBolt AI platform packaged for resale. ${PRODUCTS_KB.find((p) => p.id === "agency").definition} It bundles every AI automation and QR-based software product under one login.`,
  },
  {
    q: "Will my clients see the MagicBolt AI name?",
    a: "No. Your brand name, logo, favicon, colours and login page run on your own domain, with a separate domain for QR pages. Clients only ever see your brand.",
  },
  {
    q: "Can I set my own prices for clients?",
    a: "Yes. You build unlimited pricing tiers, choose which features each tier unlocks, set monthly usage caps, bill clients directly and keep 100% of the margin.",
  },
  {
    q: "Do I need to run servers or hosting?",
    a: "No. The platform is fully managed and cloud-hosted. You can also bring your own Google Maps, AI provider, SMTP and storage keys so usage bills to you directly.",
  },
  {
    q: "What help do I get to launch?",
    a: "Every plan includes white-label branding and custom domain setup, technical setup assistance, a dedicated launch manager, a sales and proposal kit, ready-to-sell service packages, and agency launch and sales training.",
  },
];

export const HOW_AGENCIES_EARN_FAQS = [
  {
    q: "How do agencies make money with MagicBolt AI?",
    a: "Agencies resell the platform under their own brand as monthly or annual software subscriptions and one-time setup fees, and bundle it with services such as SEO, social media management, website and WhatsApp CRM setup, review and reputation management, and lead generation.",
  },
  {
    q: "Who decides what my clients pay?",
    a: "You do. MagicBolt AI charges you for your plan; what you charge your clients - and the margin you keep - is entirely up to you.",
  },
  {
    q: "Which businesses can I sell to?",
    a: `Any local business that relies on Google, social media, reviews or bookings. Common fits include ${INDUSTRIES.map((i) => i.name.toLowerCase()).join(", ")}.`,
  },
  {
    q: "Do I get help finding clients?",
    a: "Yes. The Lead Center finds businesses on Google Maps with an optimisation score for each profile, and bulk mailing runs outreach through your own email. Plans also include a lead generation strategy, leads suggestion, sales and outreach scripts, and 500+ leads of potential clients in your city.",
  },
];

export const ABOUT_FAQS = [
  {
    q: "Which company owns MagicBolt AI?",
    a: `MagicBolt AI is a product of ${COMPANY.legalName}, ${COMPANY.address.streetAddress}, ${COMPANY.address.addressLocality}, ${COMPANY.address.addressRegion} ${COMPANY.address.postalCode}, India.`,
  },
  { q: "Which countries does MagicBolt AI serve?", a: SERVICE_AREA },
  { q: "What is MagicBolt AI's refund policy?", a: REFUND_SUMMARY },
  {
    q: "Is there a mobile app?",
    a: "Yes. Alongside the web dashboard, MagicBolt AI has an Android app on Google Play.",
  },
];

export const CONTACT_FAQS = [
  {
    q: "How do I contact MagicBolt AI support?",
    a: `Email ${COMPANY.email} or send a WhatsApp message to ${COMPANY.phone}. The links on this page open both directly.`,
  },
  {
    q: "How do I request a refund?",
    a: `Email ${COMPANY.email} with your account email and the reason for the request. Each request is reviewed individually; approved refunds reach the original payment method within 7-10 business days.`,
  },
  {
    q: "Where is your office?",
    a: `${COMPANY.legalName}, ${COMPANY.address.streetAddress}, ${COMPANY.address.addressLocality}, ${COMPANY.address.addressRegion} ${COMPANY.address.postalCode}, India.`,
  },
  {
    q: "Do you work with customers outside India?",
    a: "Yes. MagicBolt AI serves agencies and businesses worldwide - the platform is cloud-based and the pricing page shows prices in several currencies.",
  },
];

// --- Product pages ----------------------------------------------------------
// Extra product-specific questions for products that have no section on
// /faq yet. Each answer restates that product's feature list.
const PRODUCT_EXTRA_FAQS = {
  appointments: [
    { q: "Does Appointment Scheduling send reminders?", a: "Yes. Booking confirmations and appointment reminders go out automatically on WhatsApp and email to cut no-shows." },
    { q: "Can customers pay a deposit when they book?", a: "Yes. You can collect full payment or a deposit at the time of booking." },
    { q: "Can I offer phone or video appointments?", a: "Yes. Appointments can be by phone, video meeting or in person, with the details sent to the customer." },
  ],
  publishing: [
    { q: "Can I post to several channels at once?", a: "Yes. One composer publishes to Google, Facebook, Instagram and WordPress together, with a live phone preview for each platform." },
    { q: "Can I schedule a first comment?", a: "Yes. A first comment and threaded replies can be posted automatically minutes or hours after the post goes live." },
    { q: "Can I schedule many photos or videos in bulk?", a: "Yes. The bulk media scheduler spreads uploads out daily, on alternate days or on a custom schedule." },
  ],
  "ai-tools": [
    { q: "Does the AI publish without my approval?", a: "Only if you want it to. AI Autopilot can hold new posts as drafts for approval or publish them automatically." },
    { q: "How does the AI learn a brand's voice?", a: "The Brand Voice Agent is taught the tone, keywords and style once, and everything it writes follows them." },
    { q: "What is the MCP connector?", a: "It lets you manage the business from Claude, ChatGPT or Cursor in read-and-draft mode - a person approves anything before it goes live." },
  ],
};

export function productFaqs(kb) {
  if (!kb) return [];
  // "What is" / "Who is it for" are answered by the page's At-a-glance block.
  return [
    ...(PRODUCT_EXTRA_FAQS[kb.id] || []),
    // Agency OS *is* the resale layer - its own page answers this in full.
    ...(kb.id === "agency" ? [] : [{
      q: `Can I resell ${kb.name} under my own brand?`,
      a: `Yes. ${kb.name} is part of Agency OS, MagicBolt AI's white-label platform: it runs under your logo, colours and domain at prices you set, and clients never see the MagicBolt AI name.`,
    }]),
    {
      q: `How much does ${kb.name} cost?`,
      a: `MagicBolt AI sells plans rather than individual products. Channel Based plans start at ${PRICE_FROM} per year; Unlimited plans (${UNLIMITED_NAMES}) can be paid monthly, yearly or once for lifetime. The pricing page shows which plans include ${kb.name}.`,
    },
  ];
}

// --- Plan pages (/pricing/:slug) ---------------------------------------------
// Answers are built from the plan's own pricing and its column in the
// comparison matrix, so they always match the pricing page.
export function planFaqs(plan) {
  const name = `${planDisplayName(plan.name)} plan`;
  const rows = planSections(plan).flatMap((s) => s.rows);
  const missing = rows.filter((r) => r.values[plan.key] === false).map((r) => r.label);
  const hasWhiteLabel = (p) => rows.find((r) => r.label === "White-Label")?.values[p.key] === true;
  const firstWhiteLabel = ALL_PLANS.find((p) => p.level === plan.level && hasWhiteLabel(p));
  // Lifetime pricing is quoted for the Unlimited plans only, matching the
  // plan pages (Channel Based plan pages leave it off).
  const { monthly, yearly, onetime } = plan.pricing;
  const cost = monthly
    ? `${formatINR(monthly.setup)} one-time setup plus ${formatINR(monthly.amount)} per month, ${formatINR(yearly.amount)} per year, or ${formatINR(onetime.amount)} once for lifetime access`
    : `${formatINR(yearly.amount)} for the first year and covers ${plan.channelAllowance.toLowerCase()}. From the next year the renewal price is ${formatINR(channelPrice(1, "yearly"))} per active channel per year`;
  return [
    {
      q: `How much does the ${name} cost?`,
      a: `The ${name} costs ${cost}. Prices are in INR and currently shown at a limited-time ${PRICING_PROMOTION.discountPercent}% discount off the list price.`,
    },
    {
      q: `What is included in the ${name}?`,
      a: `The ${name} includes ${planHighlights(plan).join(", ")}. Every plan also ships with bundled extras: ${ADDONS.map((g) => g.title).join("; ")}.`,
    },
    {
      q: `Is white-label included in the ${name}?`,
      a: hasWhiteLabel(plan)
        ? `Yes. The ${name} includes the white-label platform with custom branding and a custom domain, so your clients only see your brand.`
        : `No. The ${name} does not include white-label, custom branding or a custom domain.${
            firstWhiteLabel ? ` These start with the ${planDisplayName(firstWhiteLabel.name)} plan.` : ""
          }`,
    },
    ...(missing.length
      ? [
          {
            q: `What is not included in the ${name}?`,
            a: `The ${name} does not include ${missing.join(", ")}. The comparison table on the pricing page shows which plans do.`,
          },
        ]
      : []),
    { q: "What is the refund policy?", a: REFUND_SUMMARY },
  ];
}
